/* eslint-disable @typescript-eslint/camelcase */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import CircleIconButton from '@fenderdigital/ui/CircleIconButton';
import usePlayUser from '@fenderdigital/custom-hooks/hooks/usePlayUser';
import { getAuthenticationHeader } from '@fenderdigital/custom-hooks/lib/cognito';
import SubscriptionCheck from '../../components/SubscriptionCheck';
import BackButton from '../../components/BackButton';
import { FeedbackBadge } from '../../components/FeedbackMode';
import useFeedbackModeModal from '../../components/FeedbackModeModal/useFeedbackModal';
import useWindowWidth from '../../hooks/useWindowWidth';
import {
  EventNames,
  trackEvent,
  trackEventAndUpdate,
  trackPageEvents,
  getAlgoliaTracking,
} from '../../lib/analytics';
import useQueueStreaksModal from '../../hooks/useQueueStreaksModal';
import { RootState } from '../../redux-types/root.redux-types';
import useDataLoader from '../../utils/useDataLoader';
import {
  getActivityProperties,
  getCurriculumProperties,
  getNextActivityProperties,
  getUserActivitiesCompleted,
} from '../../utils/activity-utils';
import { getMmsTopScore, setMmsTopScore } from '../../lib/feedback-mode';
import bugsnagClient from '../../core/bugsnag';
import { getFlag } from '../../lib/feature-flags';
import { isLocked } from '../../utils/access-utils';

const FrameContainer = styled.div`
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const MMSFrame = styled.iframe`
  border: none;
  width: 100%;
  height: 83%;
  @media screen and (min-height: 360px) {
    height: 85%;
  }
  @media screen and (min-height: 410px) {
    height: 87%;
  }
  @media screen and (min-height: 568px) {
    height: 94%;
  }
  @media screen and (min-height: 730px) {
    height: 95%;
  }
  ${up('laptop')} {
    height: 90%;
  }
`;

const AbsFlex = styled.div<{ shrink: boolean }>`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  right: 50%;
  left: 50%;
  top: ${({ shrink }): string => (shrink ? `7px` : `11px`)};
`;

const Header = styled.div`
  margin-top: 0.5rem;
  display: flex;
  position: relative;

  @media screen and (min-width: 823px) {
    display: grid;
    grid-template-columns: 2.3fr 64px minmax(30rem, 64rem) 2.3fr;
    grid-template-areas: 'space space1 content space2';
  }
`;

const HeaderFlex = styled.div`
  display: flex;
  grid-area: content;
  padding: 0 1.2em 0;
  width: 100%;
  justify-content: space-between;
  ${up('tablet-portrait')} {
    padding-right: 4rem;
  }
  ${up('laptop')} {
    padding-right: 5rem;
    max-width: 64em;
  }
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FeedbackMode: React.FC<{ match: { params: any } }> = ({ match }) => {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const selector = (state: RootState) => ({
    myProgress: state.myProgress,
    paths: state.paths.watching,
    myPath: state.myPath,
    currentLesson: state.paths.watching.currentLesson,
    nextLesson: state.paths.watching.nextLesson,
  });
  const { paths, myProgress, myPath, currentLesson, nextLesson } = useSelector(selector);

  const [nextActivityProperties, setNextActivityProperties] = useState({});
  const [activityProperties, setActivityProperties] = useState({});
  const [curriculumProperties, setCurriculumProperties] = useState({});
  const [userActivitiesCompleted, setUserActivitiesCompleted] = useState({});

  const [srcDoc, setSrcDoc] = useState('');
  const [mmsId, setmmsId] = useState('');
  const [courseId, setCourseId] = useState('');
  const [courseTitle, setCourseTitle] = useState('');
  const [repeatTimes, setRepeatTimes] = useState(0);
  const [activityCompleted, setActivityCompleted] = useState(false);
  const [feedbackModeScoreReceived, setFeedbackModeScoreReceived] = useState({
    score: 0,
    rhythm_score: 0,
    note_score: 0,
    tempo_score: 0,
    match_percent: 0,
  });
  const [topScore, setTopScore] = useState(0);
  const [startFeedbackMode, setStartFeedbackMode] = useState(true);
  const { score, rhythm_score, note_score, tempo_score, match_percent } = feedbackModeScoreReceived;
  const history = useHistory();
  const { user, accessLevel: userAccessLevel } = usePlayUser();
  const { isFetching }: { isFetching: boolean } = useDataLoader();
  const isPlay5 = getFlag('isPlay5');

  const { courseSlug } = match?.params;

  const {
    showFeedbackModeModal,
    setFeedbackModeState,
    getFeedbackModeState,
  } = useFeedbackModeModal();

  const hasSeenFeedbackModeModal = getFeedbackModeState();

  const globalProperties = {
    fc_id: user?.userID,
    user_id: user?.userID,
  };
  const activityType = 'feedback-mode';
  const isOnPath = myPath?.inProgress;
  const trackingActivityProperties = {
    ...globalProperties,
    ...activityProperties,
    activity_type: activityType,
  };

  const updateHighScore = async (highScore: number): Promise<void> => {
    try {
      await setMmsTopScore(mmsId, highScore);
    } catch (err) {
      console.error(err);
      bugsnagClient.notify(err);
    }
  };

  const trackingFeedbackScoreReceived = (event: {
    data: {
      ename: string;
      evalue: {
        score: number;
        rhythm_score: number;
        note_score: number;
        tempo_score: number;
        match_percent: number;
      };
    };
  }): void => {
    if (event.data.ename !== 'feedback_available') return;
    setFeedbackModeScoreReceived(event.data.evalue);
    setActivityCompleted(true);
    setRepeatTimes(repeat => repeat + 1);
    const currentScore = event.data.evalue.score;
    if (currentScore > topScore && mmsId) {
      setTopScore(currentScore);
      updateHighScore(currentScore);
    }
  };

  const feedbackModeStarted = (event: { data: { ename: string } }): void => {
    if (event.data.ename === 'record_start') {
      setStartFeedbackMode(true);
    }
    if (event.data.ename === 'modals_showing') {
      setStartFeedbackMode(false);
    }
  };

  useQueueStreaksModal();

  useEffect(() => {
    window.addEventListener('message', feedbackModeStarted);
    window.addEventListener('message', trackingFeedbackScoreReceived);
    return (): void => {
      window.removeEventListener('message', feedbackModeStarted);
      window.removeEventListener('message', trackingFeedbackScoreReceived);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mmsId]);

  useEffect(() => {
    if (!hasSeenFeedbackModeModal) {
      showFeedbackModeModal();
      setFeedbackModeState();
    }
  }, [mmsId, hasSeenFeedbackModeModal, showFeedbackModeModal, setFeedbackModeState]);

  useEffect(() => {
    const getMMSId = async (): Promise<void> => {
      // eslint-disable-next-line max-len
      const url = `${process.env.REACT_APP_CLIENT_LIBRARY_API}/courses?offset=0&limit=20&sortField=start-at&sortOrder=asc&slug=${courseSlug}`;
      const Authorization = await getAuthenticationHeader();
      const method = 'GET';
      const params = {
        method,
        headers: {
          Authorization,
        },
      };
      const res = await fetch(url, params);
      const [course] = await res.json();
      const courseAccessLevel = course?.['access-levels'];
      const isAvailable = !(isPlay5 && isLocked(courseAccessLevel, userAccessLevel));
      if (!isAvailable) {
        history.replace('/plans/upgrade');
      } else {
        const id = course['mms-id'];
        setCourseId(course.id);
        setCourseTitle(course.title);
        setmmsId(id);
      }
    };
    !mmsId && getMMSId();
  }, [courseSlug, mmsId, history, isPlay5, userAccessLevel]);

  useEffect(() => {
    const getHighScore = async (): Promise<void> => {
      try {
        const highestScore = await getMmsTopScore(mmsId);
        setTopScore(highestScore);
      } catch (err) {
        console.error(err);
        bugsnagClient.notify(err);
        setTopScore(0);
      }
    };
    mmsId && getHighScore();
  }, [mmsId]);

  useEffect(() => {
    const getPage = async (): Promise<void> => {
      const url = `${process.env.REACT_APP_MMS_API}?mms-id=${mmsId}`;
      const Authorization = await getAuthenticationHeader();
      const method = 'GET';
      const params = {
        method,
        headers: {
          Authorization,
        },
      };
      const res = await fetch(url, params);
      const html = await res.text();
      setSrcDoc(html);
    };
    mmsId && getPage();
  }, [mmsId]);

  useEffect(() => {
    if (!isFetching) {
      const currentPathStyle = myPath.style;
      const activityPropertiesEvent = getActivityProperties(currentLesson, currentPathStyle);
      const curriculumPropertiesEvent = getCurriculumProperties(paths);
      const userActivitiesCompletedEvent = getUserActivitiesCompleted(myProgress, myPath);
      const nextActivityEvent = getNextActivityProperties(nextLesson);
      setActivityProperties(activityPropertiesEvent);
      setCurriculumProperties(curriculumPropertiesEvent);
      setUserActivitiesCompleted(userActivitiesCompletedEvent);
      setNextActivityProperties(nextActivityEvent);
    }
  }, [isFetching, myPath, myProgress, currentLesson, paths, nextLesson]);

  useEffect(() => {
    mmsId &&
      user?.userID &&
      trackPageEvents(EventNames.FEEDBACK_MODE, {
        fc_id: user?.userID,
        course_id: courseId,
        course_slug: courseSlug,
        course_name: courseTitle,
        mms_resource_link_id: mmsId,
        ...getAlgoliaTracking('view', courseId),
      });
  }, [courseId, courseSlug, courseTitle, mmsId, user]);

  useEffect(() => {
    mmsId &&
      user?.userID &&
      startFeedbackMode &&
      trackEventAndUpdate(
        user?.userID,
        EventNames.ACTIVITY_STARTED,
        {
          ...trackingActivityProperties,
          repeat: repeatTimes,
          ...getAlgoliaTracking('click', courseId),
        },
        curriculumProperties,
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startFeedbackMode]);

  useEffect(() => {
    mmsId &&
      user?.userID &&
      score &&
      trackEvent(EventNames.FEEDBACK_MODE_SCORE_RECEIVED, {
        fc_id: user?.userID,
        course_id: courseId,
        course_slug: courseSlug,
        course_name: courseTitle,
        mms_score_overall: score,
        mms_score_rhythm: rhythm_score,
        mms_score_pitch: note_score,
        mms_length_played_percentage: match_percent,
        mms_resource_link_id: mmsId,
        mms_score_tempo: tempo_score,
      });
  }, [
    courseSlug,
    match_percent,
    note_score,
    rhythm_score,
    score,
    tempo_score,
    mmsId,
    user,
    courseTitle,
    courseId,
  ]);

  useEffect(() => {
    if (score)
      mmsId &&
        user?.userID &&
        activityCompleted &&
        trackEventAndUpdate(
          user?.userID,
          EventNames.ACTIVITY_COMPLETED,
          {
            ...trackingActivityProperties,
            ...nextActivityProperties,
            activity_score: score,
            is_on_path: isOnPath,
            ...getAlgoliaTracking('conversion', courseId),
          },
          userActivitiesCompleted,
        );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityProperties, score, activityCompleted]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const width = useWindowWidth();
  const shrinkHeader = width < 500;

  const iframeBackHandler = (): void => {
    trackEvent(EventNames.ACTIVITY_DISMISSED, {
      ...trackingActivityProperties,
    });
    const directLinkToFeedbackMode = history.length === 2 || history.action === 'REPLACE';
    if (directLinkToFeedbackMode) {
      history.push('/');
    } else {
      history.go(-2);
    }
  };

  return (
    <>
      <FrameContainer data-id="frame-container">
        <Header>
          <HeaderFlex>
            {shrinkHeader ? (
              <CircleIconButton
                ariaLabel="Back"
                height="2rem"
                width="2rem"
                iconName="PointerThickLeft"
                borderColor="lightGray"
                borderWidth="1px"
                iconSize="f4"
                inlineIconStyles={{ fill: '#666', paddingRight: '3px' }}
                onClick={(): void => {
                  iframeBackHandler();
                }}
              />
            ) : (
              <BackButton onClick={iframeBackHandler} preventDefaultBack />
            )}
            <AbsFlex shrink={shrinkHeader}>
              <FeedbackBadge label="feedback mode" prettyLabel="beta" absolute />
            </AbsFlex>
          </HeaderFlex>
        </Header>
        {srcDoc && (
          <MMSFrame id="fd-iframe" title="feedback" allow="microphone *" srcDoc={srcDoc} />
        )}
      </FrameContainer>
    </>
  );
};

export default SubscriptionCheck(FeedbackMode);
